import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Input,
  Card,
  Row,
  Col,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  RightCircleOutlined,
  SearchOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { API } from "../../../Axios/Axios";
import {
  fetchReportData,
  StartPauseScheduleById,
} from "../../../Helpers/APIEndpoints/Endpoints";
import { useAuthStore } from "../../../AuthStore/AuthStore";
import useDebounce from "../../../Helpers/Debounce/useDebounce";

const { Title } = Typography;

export default function SchedulerReport() {
  const navigate = useNavigate();
  const { commonContextParams } = useAuthStore();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 2000);

  const reportId = 17;

  const getReportData = async (
    pageNumber = 1,
    pageSizeVal = 10,
    sortFieldVal = sortField,
    sortOrderVal = sortOrder,
    search = debouncedSearchText
  ) => {
    setLoading(true);
    try {
      const response = await API.post(fetchReportData, {
        UserId: commonContextParams.userId,
        RoleId: commonContextParams.roleId,
        PageNumber: pageNumber,
        PageSize: pageSizeVal,
        sortBy: sortFieldVal,
        desc: sortOrderVal === "descend",
        ReportId: reportId,
        SearchText: search,
      });

      const items = response?.data?.data ?? [];

      if (items.length > 0) {
        const updatedData = items.map((item) => ({
          ...item,
          key: item.Key,
        }));
        setData(updatedData);
        setTotalCount(updatedData[0].TotalCount ?? 0);
        setPage(pageNumber);
        setPageSize(pageSizeVal);
        setSortField(sortFieldVal);
        setSortOrder(sortOrderVal);
      } else {
        setData([]);
        setTotalCount(0);
        setPage(1);
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to load data");
      setData([]);
      setTotalCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    getReportData();
  }, []);

  const handleTableChange = (pagination, filters, sorter) => {
    getReportData(
      pagination.current,
      pagination.pageSize,
      sorter.field,
      sorter.order,
      debouncedSearchText
    );
  };

  useEffect(() => {
    getReportData();
  }, [debouncedSearchText]);

  const StartStopScheduler = async (actionName: string, id: number) => {
    const actionMap = {
      start: 0,
      pause: 1,
      resume: 2,
      delete: 3,
    };

    const actionCode = actionMap[actionName.toLowerCase()];
    if (typeof actionCode !== "number") return; // ✅ Allow 0

    try {
      const response = await API.post(StartPauseScheduleById, {
        RId: id,
        Action: actionCode,
      });

      const result = response.data;

      if (result.status === 0) {
        message.success(
          `${
            actionName.charAt(0).toUpperCase() + actionName.slice(1)
          }d successfully`
        );
        getReportData(page, pageSize, sortField, sortOrder, searchText); // Refresh after action
      } else {
        message.error("Something went wrong");
      }
    } catch (error) {
      console.error("Action failed:", error);
      message.error("Failed to update scheduler");
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "Title",
      key: "Title",
      sorter: true,
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      ellipsis: true,
    },
    {
      title: "Cron Expression",
      dataIndex: "Cron",
      key: "Cron",
      ellipsis: true,
    },
    {
      title: "Active",
      dataIndex: "Active",
      key: "Active",
      render: (active: string) => (active === "Yes" ? "Yes" : "No"),
      // filters: [
      //   { text: "Yes", value: "Yes" },
      //   { text: "No", value: "No" },
      // ],
      // onFilter: (value, record) => record.Active === value,
      sorter: true,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const status = record.Status;

        return (
          <Space size="middle">
            <Button
              icon={<EditOutlined />}
              type="link"
              onClick={() =>
                navigate(`/ScheduleCreate`, { state: { RId: record?.key } })
              }
              disabled={!(status === 1 || status === 3)} // Disable edit if deleted
            />
            <Button
              icon={<RightCircleOutlined />}
              type="link"
              onClick={() => StartStopScheduler("Start", record.key)}
              disabled={status !== null} // Only allow start if status is null and deleted
            />
            <Button
              icon={<PauseCircleOutlined />}
              type="link"
              onClick={() => StartStopScheduler("Pause", record.key)}
              disabled={!(status === 0 || status === 2)} // ✅ Only allow pause if started or resumed
            />
            <Button
              icon={<ReloadOutlined />}
              type="link"
              onClick={() => StartStopScheduler("Resume", record.key)}
              disabled={status !== 1} // Only allow resume if paused
            />
            <Button
              icon={<DeleteOutlined />}
              type="link"
              danger
              onClick={() => StartStopScheduler("Delete", record.key)}
              disabled={status === 3} // Disable if already deleted
            />
          </Space>
        );
      },
    },
  ];

  return (
    <Card style={{ margin: 20 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Schedule Report
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/ScheduleCreate")}
            >
              Create
            </Button>
            <Input
              placeholder="Search by title"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 300 }}
            />
          </Space>
        </Col>
      </Row>

      <Table
        bordered
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        rowKey="key"
      />
    </Card>
  );
}
